<script setup>
import { computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { Icon } from '@iconify/vue';

const props = defineProps({ item: Object, level: Number });

const router = useRouter();
const route = useRoute();

/** /register 가 /register/current·/register/my 까지 active 되지 않도록 */
function isMenuPathActive(itemTo, currentPath) {
  if (!itemTo || typeof itemTo !== 'string') return false;
  const to = itemTo.replace(/\/+$/, '') || '/';
  const path = (currentPath || '').replace(/\/+$/, '') || '/';
  if (path === to) return true;
  if (to === '/register') {
    return (
      path.startsWith('/register/')
      && !path.startsWith('/register/current')
      && !path.startsWith('/register/my')
    );
  }
  // /tasks(T-Task) 가 형제 메뉴의 하위 경로 — /tasks/tsign-history(T-Sign)·/tasks/tfiling-history(T-Filing)
  // — 에서까지 active 되지 않도록 제외. (task 상세 등 그 외 /tasks/* 는 T-Task active 유지)
  if (to === '/tasks') {
    return (
      path.startsWith('/tasks/')
      && !path.startsWith('/tasks/tsign-history')
      && !path.startsWith('/tasks/tfiling-history')
    );
  }
  return path === to || path.startsWith(`${to}/`);
}

const isActive = computed(() => {
  if (props.item?.type === 'external' || !props.item?.to) return false;
  return isMenuPathActive(props.item.to, route.path);
});

// 현재 메뉴 클릭 시 리로드 처리
const handleClick = (e) => {
  if (props.item.type === 'external') return;

  // 현재 경로와 클릭한 메뉴의 경로가 같으면 데이터 재조회
  if (route.path === props.item.to) {
    e.preventDefault();
    // _refresh 파라미터를 추가하여 컴포넌트 재마운트 및 데이터 재조회
    router.replace({ path: props.item.to, query: { _refresh: Date.now() } });
  }
};

const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);

// 언어 변경 시 자동으로 업데이트되도록 computed로 감싸기
const menuTitle = computed(() => {
  // titleKey가 있으면 우선 사용
  if (props.item.titleKey) {
    const translated = t(props.item.titleKey);
    // 번역 키가 그대로 반환된 경우 (번역을 찾지 못한 경우) 원본 title 사용
    if (translated === props.item.titleKey) {
      return props.item.title;
    }
    return translated || props.item.title;
  }
  // titleKey가 없으면 기존 방식 사용
  const translationKey = 'menu.' + props.item.title;
  const translated = t(translationKey);
  // 번역 키가 그대로 반환된 경우 (번역을 찾지 못한 경우) 원본 title 사용
  if (translated === translationKey || translated.startsWith('menu.')) {
    return props.item.title;
  }
  return translated || props.item.title;
});
</script>

<template>
    <!---Single Item-->
    <v-list-item
        :to="item.type === 'external' ? '' : item.to"
        :href="item.type === 'external' ? item.to : ''"
        :active="isActive"
        rounded="pill"
        class="mb-1"
        :style="item.chip ? 'overflow: visible !important; padding-right: 8px;' : ''"
        :disabled="item.disabled"
        :target="item.type === 'external' ? '_blank' : ''"
        v-scroll-to="{ el: '#top' }"
        @click="handleClick"
    >
        <!---If icon-->
        <template v-slot:prepend>
            <Icon :icon="'solar:' + item.icon" height="19" class="dot" :class="'text-' + item.BgColor" :level="level"/>
        </template>
        <v-list-item-title class="d-flex align-center ga-2">
            {{ menuTitle }}
            <!---Beta chip을 제목 옆(왼쪽)에 배치-->
            <v-chip
                v-if="item.chip"
                :color="item.chipColor"
                :class="'sidebarchip bg-' + item.chipBgColor"
                size="x-small"
                :variant="item.chipVariant || 'flat'"
                :prepend-icon="item.chipIcon"
                style="font-weight: 600; font-size: 9px; height: 16px; flex-shrink: 0;"
            >
                {{ item.chip }}
            </v-chip>
        </v-list-item-title>
        <!---If Caption-->
        <v-list-item-subtitle v-if="item.subCaption" class="text-caption mt-n1 hide-menu">
            {{ item.subCaption }}
        </v-list-item-subtitle>
    </v-list-item>
</template>
